/**
 * Clients → Targets (prototype VIEWS.targets + tgEdit / tgAdd / tgImport, docs/prototype.html 2144–2209).
 * Where each client platform stands against its contract target, grouped by SPOC, most behind first. Head
 * actions "Paste from sheet" and "+ Add a row"; four stats (hidden in the Clients hub by the prototype CSS, as
 * there); the source note; SPOC pills and status pills; one panel per SPOC with the tracker-sheet columns.
 *
 * Data: GET /targets?quarter (managers all; a member their own clients — the server scopes it). Writes are for
 * managers only (`clients.edit.all`; ADR §11.8, step-6 decision 2 — the prototype's "SPOCs can correct a row"
 * is superseded): POST /clients/{id}/targets, PATCH /clients/{id}/targets/{tid} with If-Match, and
 * POST /targets/import (dry run first, so rows the server cannot match are shown before anything is written).
 * Balance, %, progress and status are the server's (`targetProgress`), never recomputed here.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  SPOC_ELIGIBLE_ROLES,
  TARGET_STATE_LABEL,
  quarterOf,
  type ClientTarget,
  type ImportTargetsResponse,
  type Quarter,
  type Role,
  type TargetState,
  type UserRef,
} from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { useClients, useUsers } from '../../lib/data';
import { todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { dayMonth } from '../../lib/timesheets';
import { nf, parseSheetRows, targetSourceWords, useCreateTarget, useImportTargets, useTargets, useUpdateTarget } from '../../lib/targets';
import { Empty, ErrorState, Field, Loading, Modal, MultiSelectFilter, Pills, errorText, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

/** Prototype tgState tones: Achieved hot, Close warm, Behind cold. */
const TONE: Record<TargetState, string> = { achieved: 'hot', close: 'warm', behind: 'cold' };
const QUARTER_RE = /^FY(\d{2})-Q([1-4])$/;

/** FY27-Q3 → FY27-Q2 (n = -1) / FY27-Q4 (n = 1). */
function shiftQuarter(q: Quarter, n: number): Quarter {
  const m = QUARTER_RE.exec(q)!;
  const i = Number(m[1]) * 4 + (Number(m[2]) - 1) + n;
  return `FY${String(Math.floor(i / 4)).padStart(2, '0')}-Q${(i % 4) + 1}`;
}
/** "FY27-Q3" → "Oct to Dec 2026" (Indian financial year: Q1 is April to June). */
function quarterLabel(q: Quarter): string {
  const m = QUARTER_RE.exec(q)!;
  const fy = 2000 + Number(m[1]);
  const n = Number(m[2]);
  const MON = ['Apr', 'Jul', 'Oct', 'Jan'];
  const END = ['Jun', 'Sep', 'Dec', 'Mar'];
  const year = n === 4 ? fy : fy - 1;
  return `${q.replace('-', ' ')} · ${MON[n - 1]} to ${END[n - 1]} ${year}`;
}

export function Targets() {
  const { can } = useSession();
  const edit = can('clients.edit.all');
  const [sp, setSp] = useSearchParams();
  const current = quarterOf(todayIso());
  const qp = sp.get('quarter');
  const quarter: Quarter = qp && QUARTER_RE.test(qp) ? qp : current;
  // ?spoc is comma-joined SPOC ids; absent = all SPOCs. GET /targets returns the whole quarter (not paged), so this filters on screen.
  const who = (sp.get('spoc') ?? '').split(',').filter(Boolean);
  const st = (['behind', 'close', 'achieved'] as const).find((s) => s === sp.get('status')) ?? 'all';
  const q = useTargets({ quarter });
  const [editing, setEditing] = useState<ClientTarget | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const set = (k: string, v: string, dflt: string) =>
    setSp((p) => {
      const n = new URLSearchParams(p);
      if (v === dflt) n.delete(k);
      else n.set(k, v);
      return n;
    });

  const all = q.data?.data ?? [];
  // The SPOC pills, in the server's order (sorted by owner, then most behind first).
  const owners = useMemo(() => {
    const m = new Map<string, UserRef>();
    for (const r of all) if (!m.has(r.owner.id)) m.set(r.owner.id, r.owner);
    return [...m.values()];
  }, [all]);
  const rows = all.filter((r) => (!who.length || who.includes(r.owner.id)) && (st === 'all' || r.state === st));
  const S = q.data?.summary;
  const source = targetSourceWords(all);
  // Prototype "last synced today": the newest row change, in words.
  const newest = all.reduce((m, r) => (r.updated_at > m ? r.updated_at : m), '');
  const newestDay = newest ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(newest)) : '';
  const lastUpdated = newestDay === todayIso() ? 'today' : newestDay ? dayMonth(newestDay) : '';
  const quarters = [1, 0, -1, -2, -3].map((n) => shiftQuarter(current, n));
  if (!quarters.includes(quarter)) quarters.push(quarter);

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px' }}>
        <span className="grow" />
        <div className="r">
          <select className="in" style={{ width: 'auto', borderRadius: 99 }} value={quarter} onChange={(e) => set('quarter', e.target.value, current)} aria-label="Quarter">
            {quarters.map((x) => (
              <option key={x} value={x}>
                {quarterLabel(x)}
                {x === current ? ' (this quarter)' : ''}
              </option>
            ))}
          </select>
          {edit ? (
            <>
              <button className="btn" onClick={() => setImporting(true)}>
                Paste from sheet
              </button>
              <button className="btn pri" onClick={() => setAdding(true)}>
                + Add a row
              </button>
            </>
          ) : null}
        </div>
      </div>

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="stats">
            <Stat k="Platforms tracked" v={S?.tracked ?? 0} />
            <Stat k="Target achieved" v={S?.achieved ?? 0} cls="green" />
            <Stat k="Behind target" v={S?.behind ?? 0} s="more than a quarter left" cls="red" />
            <Stat k="Balance still to deliver" v={nf(S?.balance ?? 0)} s="across all platforms" />
          </div>
          {all.length ? (
            <div className="note" style={{ marginBottom: 14 }}>
              <b>Source:</b> {source.words}, last updated {lastUpdated}
              {S?.as_of ? ` (figures as of ${dayMonth(S.as_of)})` : ''}. {edit ? 'You can correct a row with Update.' : 'Your manager corrects the figures.'}
              {source.fromSheet ? ' Changes here are not written back to the sheet, so tell its owner.' : null}
            </div>
          ) : null}
          <div className="bar">
            <MultiSelectFilter
              label="SPOC"
              allLabel="All SPOCs"
              options={owners.map((o) => ({ value: o.id, label: o.name, count: all.filter((r) => r.owner.id === o.id).length }))}
              value={who}
              onChange={(v) => set('spoc', v.join(','), '')}
            />
            <span className="grow" />
            <Pills<TargetState | 'all'>
              options={[
                ['all', 'Any status'],
                ['behind', 'Behind'],
                ['close', 'Close'],
                ['achieved', 'Achieved'],
              ]}
              value={st}
              onChange={(v) => set('status', v, 'all')}
            />
          </div>
          {!all.length ? (
            <div className="panel">
              <Empty title={`No targets for ${quarter.replace('-', ' ')}`}>
                {edit ? 'Add a row, or paste the rows from the tracker sheet.' : 'Targets for your clients show here once a manager adds them.'}
              </Empty>
            </div>
          ) : rows.length ? (
            owners
              .filter((o) => rows.some((r) => r.owner.id === o.id))
              .map((o) => {
                const rs = rows.filter((r) => r.owner.id === o.id).sort((a, b) => b.pct - a.pct);
                return (
                  <div key={o.id} className="panel" style={{ marginBottom: 14, overflowX: 'auto' }}>
                    <div className="hd">
                      <h3>{o.name}</h3>
                      <div className="r">
                        <span className="sub">
                          {rs.filter((r) => r.state === 'behind').length} behind of {rs.length}
                        </span>
                      </div>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Client platform</th>
                          <th className="tr">User base</th>
                          <th className="tr">So far</th>
                          <th className="tr">Target</th>
                          <th style={{ minWidth: 170 }}>Progress</th>
                          <th className="tr">Balance</th>
                          <th className="tr">Balance %</th>
                          <th>Status</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {rs.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <b>{r.label}</b>
                              {r.label !== r.client.name ? <div className="sub">{r.client.name}</div> : null}
                              {r.note ? <div className="sub">{r.note}</div> : null}
                            </td>
                            <td className="tr num">{nf(r.base_value)}</td>
                            <td className="tr num">{nf(r.current_value)}</td>
                            <td className="tr num">{nf(r.target_value)}</td>
                            <td>
                              <div className={`meter ${TONE[r.state]}`}>
                                <i style={{ width: `${r.done_pct}%` }} />
                              </div>
                            </td>
                            <td className="tr num">{r.balance ? nf(r.balance) : '-'}</td>
                            <td className="tr num">{r.balance ? `${r.pct}%` : '-'}</td>
                            <td>
                              <span className={`chip ${TONE[r.state]}`}>{TARGET_STATE_LABEL[r.state]}</span>
                            </td>
                            <td className="tr">
                              {edit ? (
                                <button className="btn sm" onClick={() => setEditing(r)}>
                                  Update
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
          ) : (
            <div className="panel">
              <Empty title="No rows match">Change the filters above.</Empty>
            </div>
          )}
        </>
      )}
      {editing ? <EditTargetModal t={editing} onClose={() => setEditing(null)} /> : null}
      {adding ? <AddTargetModal quarter={quarter} onClose={() => setAdding(false)} /> : null}
      {importing ? <ImportModal quarter={quarter} owners={owners} onClose={() => setImporting(false)} /> : null}
    </>
  );
}

const num = (s: string) => (s.trim() === '' ? NaN : Number(s.replace(/[,\s]/g, '')));

/** The people who can own a target: active SPOC-eligible users (the server rejects anyone else, 422). */
function useOwners() {
  const users = useUsers(true);
  const list = useMemo(
    () => (users.data ?? []).filter((u) => u.status === 'active' && (SPOC_ELIGIBLE_ROLES as readonly Role[]).includes(u.role)).sort((a, b) => a.name.localeCompare(b.name)),
    [users.data],
  );
  return { list, isPending: users.isPending };
}

// ---------------------------------------------------------------- Update (prototype tgEdit)

function EditTargetModal({ t, onClose }: { t: ClientTarget; onClose: () => void }) {
  const toast = useToast();
  const upd = useUpdateTarget();
  const [now, setNow] = useState(String(t.current_value));
  const [target, setTarget] = useState(String(t.target_value));
  const [base, setBase] = useState(t.base_value == null ? '' : String(t.base_value));
  const [note, setNote] = useState(t.note ?? '');
  const [err, setErr] = useState<Record<string, string>>({});

  const save = () => {
    const n = num(now);
    const tg = num(target);
    const b = num(base);
    if (isNaN(n) || isNaN(tg)) return setErr({ current_value: isNaN(n) ? 'Enter the number so far' : '', target_value: isNaN(tg) ? 'Enter the target' : '' });
    const body: Parameters<typeof upd.mutate>[0]['body'] = {};
    if (n !== t.current_value) body.current_value = n;
    if (tg !== t.target_value) body.target_value = tg;
    const nb = base.trim() === '' ? null : b;
    if (nb !== t.base_value && !(nb !== null && isNaN(nb))) body.base_value = nb;
    if ((note.trim() || null) !== t.note) body.note = note.trim() || null;
    if (!Object.keys(body).length) return onClose();
    upd.mutate(
      { clientId: t.client.id, id: t.id, version: t.version, body },
      {
        onSuccess: (r) => {
          toast(`Saved. Balance is now ${nf(r.balance)}.`);
          onClose();
        },
        onError: (e) => {
          if (e instanceof ApiFailure && e.status === 412) toast('Someone else changed this row. Close it and open it again to see the latest.');
          else if (e instanceof ApiFailure && Object.keys(e.fieldErrors).length) setErr(e.fieldErrors);
          else toast(errorText(e));
        },
      },
    );
  };

  return (
    <Modal
      title={t.label}
      sub={`${t.owner.name}. Balance is worked out for you.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={upd.isPending}>
            Save
          </button>
        </>
      }
    >
      <div className="cols c2">
        <Field label="So far" error={err.current_value}>
          <input className="in" type="number" min={0} value={now} onChange={(e) => setNow(e.target.value)} />
        </Field>
        <Field label="Target" error={err.target_value}>
          <input className="in" type="number" min={0} value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
      </div>
      <div className="cols c2">
        <Field label="User base" error={err.base_value}>
          <input className="in" type="number" min={0} value={base} onChange={(e) => setBase(e.target.value)} />
        </Field>
        <div />
      </div>
      <Field label="Note (optional, e.g. why it is behind)" error={err.note}>
        <input className="in" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- Add a platform (prototype tgAdd)

function AddTargetModal({ quarter, onClose }: { quarter: Quarter; onClose: () => void }) {
  const toast = useToast();
  const add = useCreateTarget();
  const clients = useClients();
  const owners = useOwners();
  const [clientId, setClientId] = useState('');
  const [label, setLabel] = useState('');
  const [owner, setOwner] = useState('');
  const [base, setBase] = useState('');
  const [now, setNow] = useState('0');
  const [target, setTarget] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const client = clients.data?.find((c) => c.id === clientId);

  const save = () => {
    const tg = num(target);
    const ownerId = owner || client?.spoc.id || '';
    const e: Record<string, string> = {};
    if (!clientId) e.client = 'Pick the client';
    if (!ownerId) e.owner_id = 'Pick the SPOC';
    if (isNaN(tg) || tg <= 0) e.target_value = 'The target must be more than 0';
    if (Object.keys(e).length) return setErr(e);
    const b = num(base);
    add.mutate(
      {
        clientId,
        body: {
          quarter,
          owner_id: ownerId,
          label: label.trim() || undefined,
          base_value: isNaN(b) ? null : b,
          current_value: isNaN(num(now)) ? 0 : num(now),
          target_value: tg,
        },
      },
      {
        onSuccess: () => {
          toast('Added');
          onClose();
        },
        onError: (er) => {
          if (er instanceof ApiFailure && er.status === 409) setErr({ label: 'This client already has a row with that name for the quarter. Give the platform its own name.' });
          else if (er instanceof ApiFailure && Object.keys(er.fieldErrors).length) setErr(er.fieldErrors);
          else toast(errorText(er));
        },
      },
    );
  };

  return (
    <Modal
      title="Add a platform"
      sub={`One row for each client platform with a target, for ${quarter.replace('-', ' ')}.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={add.isPending}>
            Add
          </button>
        </>
      }
    >
      <Field label="Client" error={err.client}>
        <select className="in" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">{clients.isPending ? 'Loading clients…' : 'Choose a client'}</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Client platform" error={err.label} hint="Leave empty to use the client’s name. Name it when a client runs more than one platform.">
        <input className="in" placeholder="e.g. HDFC Life Website" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
      </Field>
      <div className="cols c2">
        <Field label="SPOC" error={err.owner_id}>
          <select className="in" value={owner || client?.spoc.id || ''} onChange={(e) => setOwner(e.target.value)}>
            <option value="">{owners.isPending ? 'Loading people…' : 'Choose a person'}</option>
            {owners.list.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="User base" error={err.base_value}>
          <input className="in" type="number" min={0} value={base} onChange={(e) => setBase(e.target.value)} />
        </Field>
      </div>
      <div className="cols c2">
        <Field label="So far" error={err.current_value}>
          <input className="in" type="number" min={0} value={now} onChange={(e) => setNow(e.target.value)} />
        </Field>
        <Field label="Target" error={err.target_value}>
          <input className="in" type="number" min={0} value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Paste from the sheet (prototype tgImport)

function ImportModal({ quarter, owners: seen, onClose }: { quarter: Quarter; owners: UserRef[]; onClose: () => void }) {
  const toast = useToast();
  const imp = useImportTargets();
  const people = useOwners();
  const [text, setText] = useState('');
  const [owner, setOwner] = useState('');
  const [preview, setPreview] = useState<ImportTargetsResponse | null>(null);
  const { rows, problems } = useMemo(() => parseSheetRows(text), [text]);
  // People already holding targets this quarter first, then everyone else who can.
  const list = useMemo(() => {
    const ids = new Set(seen.map((s) => s.id));
    return [...people.list.filter((u) => ids.has(u.id)), ...people.list.filter((u) => !ids.has(u.id))];
  }, [people.list, seen]);

  const summary = (r: ImportTargetsResponse) =>
    `${r.updated} updated${r.added ? `, ${r.added} added` : ''}${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`;

  const run = (dry: boolean) =>
    imp.mutate(
      { quarter, rows, default_owner_id: owner || undefined, dry_run: dry },
      {
        onSuccess: (r) => {
          if (r.dry_run) {
            // Nothing the server cannot match: write straight away. Otherwise show the rows it will skip first.
            if (!r.skipped.length) run(false);
            else setPreview(r);
            return;
          }
          toast(summary(r));
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );

  const go = () => {
    if (problems.length) return toast(`Fix the ${problems.length === 1 ? 'line' : `${problems.length} lines`} that cannot be read first, or delete ${problems.length === 1 ? 'it' : 'them'}.`);
    if (!rows.length) return toast('No rows found. Paste Client, User base, So far, Target.');
    run(true);
  };
  const willWrite = preview ? preview.updated + preview.added : 0;

  return (
    <Modal
      title="Paste from the sheet"
      sub={
        <>
          Copy the rows from your tracker sheet and paste them here. Use the columns <b>Client, User base, So far, Target</b>. Two columns (<b>Client, So far</b>) update only the
          count. For a client with more than one platform, add a <b>Platform</b> column after the client and start with a header row (<b>Client, Platform, …</b>).
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? 'Back' : 'Cancel'}
          </button>
          {preview ? (
            <button className="btn pri" onClick={() => run(false)} disabled={imp.isPending || !willWrite}>
              {willWrite ? `Update the other ${willWrite}` : 'Nothing to update'}
            </button>
          ) : (
            <button className="btn pri" onClick={go} disabled={imp.isPending || problems.length > 0}>
              Update
            </button>
          )}
        </>
      }
    >
      {preview ? (
        <>
          <div className="note warn" style={{ marginBottom: 12 }}>
            {preview.skipped.length} of {preview.rows.length} rows cannot be used and will be skipped. The rest would update {preview.updated} and add {preview.added}. Nothing is
            written until you press Update.
          </div>
          <table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Client</th>
                <th>Why it is skipped</th>
              </tr>
            </thead>
            <tbody>
              {preview.skipped.map((s) => (
                <tr key={s.row}>
                  <td className="num">{s.row}</td>
                  <td>
                    <b>{s.client}</b>
                  </td>
                  <td>{s.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <>
          <textarea
            className="in"
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'SBI LM\t252932\t1485221\t1600000\nNIPPON\t57201\t121302\t130000'}
          />
          <div className="sub" style={{ fontSize: 12.5, marginTop: 6 }}>
            {text.trim() ? `${rows.length} row${rows.length === 1 ? '' : 's'} found for ${quarter.replace('-', ' ')}.` : `Rows go into ${quarter.replace('-', ' ')}.`}
          </div>
          {problems.length ? (
            <div className="note warn" role="alert" style={{ marginTop: 8 }}>
              <b>
                {problems.length === 1 ? '1 line cannot be read' : `${problems.length} lines cannot be read`}. Fix or delete {problems.length === 1 ? 'it' : 'them'} to update.
              </b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {problems.slice(0, 8).map((p) => (
                  <li key={p.line}>
                    Line {p.line} ({p.text.length > 40 ? `${p.text.slice(0, 40)}…` : p.text}): {p.why}
                  </li>
                ))}
                {problems.length > 8 ? <li>and {problems.length - 8} more.</li> : null}
              </ul>
            </div>
          ) : null}
          <Field label="SPOC for new platforms" hint="Needed only for clients that have no row yet. Each new row also needs a target.">
            <select className="in" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">{people.isPending ? 'Loading people…' : 'Nobody (skip new platforms)'}</option>
              {list.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
    </Modal>
  );
}
